import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EarningsOverview = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('week');

  const earningsData = {
    week: {
      total: 1842.50,
      jobs: 8,
      hours: 42.5,
      average: 43.35,
      pending: 425.00,
      paid: 1417.50,
      breakdown: [
        { day: 'Ma', amount: 336.00, hours: 8 },
        { day: 'Di', amount: 294.00, hours: 7 },
        { day: 'Wo', amount: 378.00, hours: 9 },
        { day: 'Do', amount: 315.00, hours: 7.5 },
        { day: 'Vr', amount: 519.50, hours: 11 }
      ]
    },
    month: {
      total: 7845.00,
      jobs: 32,
      hours: 178,
      average: 44.07,
      pending: 1250.00,
      paid: 6595.00,
      breakdown: [
        { week: 'Week 1', amount: 1842.50, hours: 42.5 },
        { week: 'Week 2', amount: 2156.00, hours: 48 },
        { week: 'Week 3', amount: 1923.50, hours: 44.5 },
        { week: 'Week 4', amount: 1923.00, hours: 43 }
      ]
    }
  };

  const currentData = earningsData?.[selectedPeriod];
  const maxAmount = Math.max(...currentData?.breakdown?.map(item => item?.amount));

  return (
    <div 
      className="bg-white border-2 border-slate-200 rounded-lg p-4 md:p-6"
      style={{
        boxShadow: '0 15px 40px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.1)'
      }}
    >
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="TrendingUp" size={20} color="var(--color-success)" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
              Verdiensten Overzicht
            </h3>
            <p className="caption text-muted-foreground">
              Uw inkomsten en betalingen
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant={selectedPeriod === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPeriod('week')}
          >
            Week
          </Button>
          <Button
            variant={selectedPeriod === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedPeriod('month')}
          >
            Maand
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted/50 rounded-lg p-3 md:p-4 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="DollarSign" size={16} color="var(--color-success)" />
            <p className="caption text-muted-foreground">Totaal</p>
          </div>
          <p className="font-heading font-semibold text-lg md:text-xl text-foreground">
            €{currentData?.total?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-muted/50 rounded-lg p-3 md:p-4 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Briefcase" size={16} color="var(--color-primary)" />
            <p className="caption text-muted-foreground">Opdrachten</p>
          </div>
          <p className="font-heading font-semibold text-lg md:text-xl text-foreground">
            {currentData?.jobs}
          </p>
        </div>

        <div className="bg-muted/50 rounded-lg p-3 md:p-4 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Clock" size={16} color="var(--color-accent)" />
            <p className="caption text-muted-foreground">Uren</p>
          </div>
          <p className="font-heading font-semibold text-lg md:text-xl text-foreground">
            {currentData?.hours}u
          </p>
        </div>

        <div className="bg-muted/50 rounded-lg p-3 md:p-4 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TrendingUp" size={16} color="var(--color-success)" />
            <p className="caption text-muted-foreground">Gem/uur</p>
          </div>
          <p className="font-heading font-semibold text-lg md:text-xl text-foreground">
            €{currentData?.average?.toFixed(2)}
          </p>
        </div>
      </div>
      <div className="mb-6">
        <h4 className="font-medium text-sm md:text-base text-foreground mb-4">
          Verdiensten Verdeling
        </h4>
        <div className="space-y-3">
          {currentData?.breakdown?.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="caption text-muted-foreground">
                  {item?.day || item?.week}
                </span>
                <div className="flex items-center gap-3">
                  <span className="caption text-muted-foreground">
                    {item?.hours}u
                  </span>
                  <span className="font-medium text-sm text-foreground whitespace-nowrap">
                    €{item?.amount?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-success transition-smooth"
                  style={{ width: `${(item?.amount / maxAmount) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-border pt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-warning/5 rounded-lg p-4 border border-warning/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={18} color="var(--color-warning)" />
              <h5 className="font-medium text-sm text-foreground">In Afwachting</h5>
            </div>
            <p className="font-heading font-semibold text-xl text-warning">
              €{currentData?.pending?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="caption text-muted-foreground mt-1">
              Wordt binnenkort uitbetaald
            </p>
          </div>

          <div className="bg-success/5 rounded-lg p-4 border border-success/20">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle" size={18} color="var(--color-success)" />
              <h5 className="font-medium text-sm text-foreground">Uitbetaald</h5>
            </div>
            <p className="font-heading font-semibold text-xl text-success">
              €{currentData?.paid?.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="caption text-muted-foreground mt-1">
              Ontvangen betalingen
            </p>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <Button variant="outline" fullWidth iconName="Download" iconPosition="left">
          Download Overzicht
        </Button>
      </div>
    </div>
  );
};

export default EarningsOverview;